import React, { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { APP_VERSION } from '../constants';
import UpdateNotification from './UpdateNotification';
import { getUpdateRemoteConfig, trackEvent } from '../lib/firebase';

const DISMISSED_VERSION_KEY = 'nammaMap:lastDismissedUpdateVersion';
const UPDATE_TARGET_VERSION_KEY = 'nammaMap:updateTargetVersion';
const UPDATE_ATTEMPTS_KEY = 'nammaMap:updateTargetAttempts';
const DEFAULT_MAX_AUTO_UPDATE_ATTEMPTS = 5;

const getStoredDismissedVersion = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(DISMISSED_VERSION_KEY);
};

const PWAUpdater: React.FC = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [availableVersion, setAvailableVersion] = useState<string | undefined>(undefined);
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(getStoredDismissedVersion);
  const [remoteTargetVersion, setRemoteTargetVersion] = useState<string | null>(null);
  const [forceUpdateEnabled, setForceUpdateEnabled] = useState(true);
  const [maxAutoUpdateAttempts, setMaxAutoUpdateAttempts] = useState(DEFAULT_MAX_AUTO_UPDATE_ATTEMPTS);
  const [updateRolloutPercent, setUpdateRolloutPercent] = useState(100);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      if (r) {
        setInterval(() => {
          r.update();
        }, 15 * 60 * 1000);
      }
    },
    onNeedRefresh() {
      fetch('/version.json', { cache: 'no-cache' })
        .then(res => res.json())
        .then(data => setAvailableVersion(data.version))
        .catch(() => setAvailableVersion(undefined));
    }
  });

  const handleUpdate = async () => {
    setIsUpdating(true);
    
    // Calculate target dynamically to ensure we don't accidentally pull an old remoteTargetVersion
    const normalizeVersion = (v: string) => Number(v.replace(/\D/g, '').padEnd(14, '0').slice(0, 14));
    const releaseOutOfDate = Boolean(remoteTargetVersion && normalizeVersion(remoteTargetVersion) > normalizeVersion(APP_VERSION));
    const inRollout = rolloutBucket < updateRolloutPercent;
    const remoteRequiresUpdate = forceUpdateEnabled && releaseOutOfDate && inRollout;
    const targetVersion = remoteRequiresUpdate ? remoteTargetVersion : availableVersion;

    if (targetVersion) {
      window.sessionStorage.setItem(UPDATE_TARGET_VERSION_KEY, targetVersion);
      window.sessionStorage.setItem(UPDATE_ATTEMPTS_KEY, '0');
      trackEvent('update_converge_started', {
        app_version: APP_VERSION,
        target_version: targetVersion,
      });
    }
    await new Promise(resolve => setTimeout(resolve, 2000));
    updateServiceWorker(true);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const isAutoAdvancing = (() => {
    if (typeof window === 'undefined') return false;
    const target = window.sessionStorage.getItem(UPDATE_TARGET_VERSION_KEY);
    return Boolean(target && target !== APP_VERSION);
  })();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const targetVersion = window.sessionStorage.getItem(UPDATE_TARGET_VERSION_KEY);
    if (!targetVersion) return;

    const normalizeVersion = (v: string) => Number(v.replace(/\D/g, '').padEnd(14, '0').slice(0, 14));
    
    // If APP_VERSION is equal to or newer than target, we are done
    if (normalizeVersion(APP_VERSION) >= normalizeVersion(targetVersion)) {
      window.sessionStorage.removeItem(UPDATE_TARGET_VERSION_KEY);
      window.sessionStorage.removeItem(UPDATE_ATTEMPTS_KEY);
      window.location.reload();
      return;
    }

    const attempts = Number(window.sessionStorage.getItem(UPDATE_ATTEMPTS_KEY) || '0');
    if (attempts >= maxAutoUpdateAttempts) {
      window.sessionStorage.removeItem(UPDATE_TARGET_VERSION_KEY);
      window.sessionStorage.removeItem(UPDATE_ATTEMPTS_KEY);
      trackEvent('update_converge_aborted', {
        app_version: APP_VERSION,
        target_version: targetVersion,
        attempts,
        max_attempts: maxAutoUpdateAttempts,
      });
      window.location.reload();
      return;
    }

    window.sessionStorage.setItem(UPDATE_ATTEMPTS_KEY, String(attempts + 1));

    const timer = window.setTimeout(() => {
      trackEvent('update_converge_attempt', {
        app_version: APP_VERSION,
        target_version: targetVersion,
        attempt: attempts + 1,
      });
      updateServiceWorker(true);
      window.location.reload();
    }, 700);

    return () => window.clearTimeout(timer);
  }, [maxAutoUpdateAttempts, updateServiceWorker]);

  useEffect(() => {
    const resolveUpdateConfig = async () => {
      const config = await getUpdateRemoteConfig();
      setRemoteTargetVersion(config.targetReleaseId);
      setForceUpdateEnabled(config.forceUpdateEnabled);
      setMaxAutoUpdateAttempts(config.maxUpdateAttempts);
      setUpdateRolloutPercent(config.updateRolloutPercent);
    };

    resolveUpdateConfig();
  }, []);

  const rolloutBucket = React.useMemo(() => {
    if (typeof window === 'undefined') return 0;

    const seed = (
      window.localStorage.getItem('nammamap_v2_device_id') ||
      window.localStorage.getItem('tvk_manifesto_device_id') ||
      `${navigator.userAgent}|${navigator.language}|${window.screen?.width || 0}x${window.screen?.height || 0}`
    );

    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = ((hash << 5) - hash) + seed.charCodeAt(i);
      hash |= 0;
    }

    return Math.abs(hash) % 100;
  }, []);

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-cache' });
        const data = await res.json();
        setAvailableVersion(data.version);
      } catch (err) {
        console.error('[VersionControl] Failed to poll version:', err);
      }
    };

    const interval = setInterval(checkVersion, 5 * 60 * 1000);
    checkVersion();

    return () => clearInterval(interval);
  }, []);

  const normalizeVersion = (v: string) => Number(v.replace(/\D/g, '').padEnd(14, '0').slice(0, 14));
  
  const releaseOutOfDate = Boolean(remoteTargetVersion && normalizeVersion(remoteTargetVersion) > normalizeVersion(APP_VERSION));
  const inRollout = rolloutBucket < updateRolloutPercent;
  const remoteRequiresUpdate = forceUpdateEnabled && releaseOutOfDate && inRollout;

  const actualTargetVersion = remoteRequiresUpdate ? remoteTargetVersion : availableVersion;

  const shouldShowUpdate =
    !isAutoAdvancing &&
    Boolean(needRefresh) &&
    (remoteRequiresUpdate || (
      availableVersion !== undefined &&
      normalizeVersion(availableVersion) > normalizeVersion(APP_VERSION)
    )) &&
    (!remoteRequiresUpdate && availableVersion ? availableVersion !== dismissedVersion : true);

  if (isAutoAdvancing) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: '#0f172a',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#f8fafc',
        padding: '20px',
        textAlign: 'center',
        fontFamily: 'Inter, system-ui, sans-serif'
      }}>
        <img 
          src="/branding/mascot.webp" 
          alt="N-Bot" 
          className="hero-mascot"
          style={{ marginBottom: '32px' }}
        />
        <div className="update-loading-dots" style={{ transform: 'scale(1.5)', marginBottom: '24px' }}>
          <span></span><span></span><span></span>
        </div>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0, letterSpacing: '-0.025em' }}>
          Installing Update
        </h2>
        <p style={{ fontSize: '0.875rem', opacity: 0.7, marginTop: '12px', maxWidth: '280px' }}>
          Please wait a moment while N-Bot applies the latest version.
        </p>
      </div>
    );
  }

  return (
    <UpdateNotification 
      show={shouldShowUpdate} 
      isUpdating={isUpdating}
      currentVersion={APP_VERSION}
      availableVersion={actualTargetVersion || undefined}
      onRefresh={handleUpdate} 
      onClose={() => {
        if (!remoteRequiresUpdate && availableVersion) {
          setDismissedVersion(availableVersion);
          window.localStorage.setItem(DISMISSED_VERSION_KEY, availableVersion);
        }
        setNeedRefresh(false);
      }}
    />
  );
};

export default PWAUpdater;
